import { useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import {
  Shield,
  Smartphone,
  Users,
  Key,
  Store,
  Package,
  RefreshCw,
  MessageSquare,
  Settings,
  Globe,
  Lock,
  Cpu,
  AlertTriangle,
  Cloud,
  ArrowRight,
  ChevronRight,
} from "lucide-react";

const TOC_ITEMS = [
  { id: "purpose", label: "What is Shomri?" },
  { id: "roles", label: "User Roles" },
  { id: "serial", label: "Device Serial" },
  { id: "auth", label: "Authentication" },
  { id: "appstore", label: "App Store Page" },
  { id: "appdetail", label: "App Detail" },
  { id: "updates", label: "Updates" },
  { id: "requests", label: "Requests" },
  { id: "settings", label: "Settings" },
  { id: "appmanagement", label: "App Management" },
  { id: "browser", label: "Browser Settings" },
  { id: "restrictions", label: "General Restrictions" },
  { id: "native", label: "Native Bridge" },
  { id: "edgecases", label: "Edge Cases" },
  { id: "services", label: "External Services" },
];

const Code = ({ children }: { children: React.ReactNode }) => (
  <code className="bg-muted/80 text-primary px-1.5 py-0.5 rounded text-sm font-mono">
    {children}
  </code>
);

const FlowArrow = () => (
  <ChevronRight className="inline w-4 h-4 text-muted-foreground mx-1 shrink-0" />
);

const FlowStep = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-flex items-center gap-0.5">{children}</span>
);

const Section = ({
  id,
  icon: Icon,
  title,
  children,
}: {
  id: string;
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}) => (
  <section id={id} className="scroll-mt-24">
    <div className="flex items-center gap-3 mb-4">
      <div className="p-2 rounded-xl bg-primary/10">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <h2 className="text-xl font-bold text-foreground">{title}</h2>
    </div>
    <div className="text-muted-foreground leading-relaxed space-y-4 pl-1">
      {children}
    </div>
  </section>
);

const InternalDocs = () => {
  const [activeSection, setActiveSection] = useState<string | null>(null);

  const scrollTo = (id: string) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-50 bg-background/80 backdrop-blur-xl border-b border-border/50">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <Shield className="w-6 h-6 text-primary" />
            <h1 className="text-lg font-bold text-foreground">
              Shomri Internal Docs
            </h1>
            <Badge variant="outline" className="text-xs">
              Staff Only
            </Badge>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 flex gap-8">
        {/* Sidebar TOC - desktop */}
        <nav className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24 space-y-1">
            <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
              Contents
            </p>
            {TOC_ITEMS.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollTo(item.id)}
                className={`block w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  activeSection === item.id
                    ? "bg-primary/10 text-primary font-medium"
                    : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </nav>

        {/* Main content */}
        <main className="flex-1 min-w-0 space-y-12 max-w-3xl">
          {/* 1. Purpose */}
          <Section id="purpose" icon={Store} title="What is Shomri?">
            <p>
              Shomri is a <strong>managed app store</strong> for iOS devices
              under MDM (Mobile Device Management) supervision. Instead of using
              Apple's native App Store, users browse, install, and request apps
              through this custom web interface.
            </p>
            <p>
              The web app runs inside a <strong>native iOS WebView</strong>{" "}
              (WKWebView). There's a Swift bridge that lets us trigger native
              behaviors — most importantly, the{" "}
              <Code>SKStoreProductViewController</Code> overlay for app
              installations.
            </p>
            <p>
              Think of it as: <em>"Apple App Store, but we control what shows up."</em>
            </p>
          </Section>

          {/* 2. User Roles */}
          <Section id="roles" icon={Users} title="User Roles & Identity">
            <p>
              There are six roles in the system. Each one determines what you
              can see and do:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                {
                  role: "Parent",
                  desc: "Manages child's device settings directly",
                },
                {
                  role: "Child",
                  desc: "Must submit requests — can't change settings",
                },
                {
                  role: "Mentor",
                  desc: "Manages mentee's device settings directly",
                },
                {
                  role: "Mentee",
                  desc: "Must submit requests — can't change settings",
                },
                {
                  role: "Self-Managed",
                  desc: "Controls own device — no Requests tab",
                },
                {
                  role: "Organization",
                  desc: "Manages multiple devices — admin access",
                },
              ].map(({ role, desc }) => (
                <div
                  key={role}
                  className="p-3 rounded-xl bg-muted/30 border border-border/50"
                >
                  <p className="font-medium text-foreground text-sm">{role}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                </div>
              ))}
            </div>

            <p className="font-medium text-foreground mt-4">
              How identity is resolved:
            </p>
            <div className="flex flex-wrap items-center gap-1 text-sm">
              <FlowStep>
                <Code>Clerk ID</Code>
              </FlowStep>
              <FlowArrow />
              <FlowStep>
                <Code>api.users.get.byClerkId</Code>
              </FlowStep>
              <FlowArrow />
              <FlowStep>
                <Code>Convex user ID</Code>
              </FlowStep>
              <FlowArrow />
              <FlowStep>
                <Code>api.users.get.byConvexId</Code>
              </FlowStep>
              <FlowArrow />
              <FlowStep>
                device + <Code>associatedUsers</Code>
              </FlowStep>
              <FlowArrow />
              <FlowStep>identity</FlowStep>
            </div>

            <Accordion type="single" collapsible>
              <AccordionItem value="identity-edge">
                <AccordionTrigger className="text-sm">
                  Edge case: Mentees without Clerk-to-Convex mapping
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    Some mentees don't have a Clerk-to-Convex user mapping. When
                    this happens, <Code>convexUserId</Code> is <Code>null</Code>,
                    the device lookup is skipped, and{" "}
                    <Code>deviceConfig</Code> stays <Code>null</Code> forever.
                  </p>
                  <p className="mt-2">
                    The app falls back to <strong>serial-based resolution</strong>{" "}
                    — the serial comes from the native header, URL, or
                    localStorage. All device-scoped queries still work because
                    they use the serial, not the Convex user ID.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Section>

          {/* 3. Device Serial */}
          <Section id="serial" icon={Smartphone} title="Device Serial Resolution">
            <p>
              The <strong>device serial</strong> is the most important piece of
              data in the whole app. Every device-scoped query uses it. Here's
              how we find it, in priority order:
            </p>
            <ol className="list-decimal list-inside space-y-2 text-sm">
              <li>
                <strong>Native header</strong> —{" "}
                <Code>__DEVICE_SERIAL__</Code> injected by the iOS app via a
                meta tag or header
              </li>
              <li>
                <strong>Query param</strong> — <Code>?cx__serial=ABC123</Code>{" "}
                in the URL
              </li>
              <li>
                <strong>Convex device lookup</strong> — if we have a Convex user
                ID, look up their device
              </li>
              <li>
                <strong>URL path</strong> — the <Code>/:deviceId</Code> route
                param (validated against{" "}
                <Code>/^[A-Z0-9]&#123;8,20&#125;$/</Code>)
              </li>
              <li>
                <strong>DeviceContext</strong> — React context that stores the
                resolved serial
              </li>
              <li>
                <strong>localStorage</strong> — last-resort fallback
              </li>
            </ol>

            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-sm mt-4">
              <p className="font-medium text-destructive">
                <AlertTriangle className="inline w-4 h-4 mr-1" />
                DeviceSerialGate
              </p>
              <p className="mt-1 text-muted-foreground">
                If no valid serial is found through any of these methods, the{" "}
                <Code>DeviceSerialGate</Code> component blocks the entire app
                and shows the <Code>NoDeviceError</Code> page. Nothing renders
                below it.
              </p>
            </div>

            <p className="text-sm mt-2">
              <strong>Validation:</strong> Serials must match{" "}
              <Code>/^[A-Z0-9]&#123;8,20&#125;$/</Code>. This rejects route
              names like "settings" or "updates" that might appear in the
              <Code>/:deviceId</Code> position.
            </p>
          </Section>

          {/* 4. Authentication */}
          <Section id="auth" icon={Key} title="Authentication Flow">
            <p>
              Authentication is handled by <strong>Clerk</strong>. The sign-in
              page lives at <Code>/auth</Code>.
            </p>
            <ul className="list-disc list-inside space-y-2 text-sm">
              <li>
                <strong>Normal flow:</strong> Email + password sign-in via Clerk
              </li>
              <li>
                <strong>Password reset:</strong> Email code flow using Clerk's{" "}
                <Code>reset_password_email_code</Code> strategy
              </li>
              <li>
                <strong>After sign-in:</strong> Redirects to the original
                intended route (stored in <Code>location.state.from</Code>)
              </li>
            </ul>

            <p className="font-medium text-foreground mt-4">AuthGuard</p>
            <p className="text-sm">
              The <Code>AuthGuard</Code> component wraps all routes except{" "}
              <Code>/auth</Code> and this docs page. If you're not signed in,
              you get redirected to <Code>/auth</Code>.
            </p>

            <Accordion type="single" collapsible>
              <AccordionItem value="mentee-bypass">
                <AccordionTrigger className="text-sm">
                  Special case: Mentee device-only mode
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    Mentees can bypass Clerk auth entirely. If{" "}
                    <Code>localStorage.mentee_device_mode === 'true'</Code> and
                    a <Code>device_serial</Code> exists in localStorage, the
                    AuthGuard lets them through without a Clerk session.
                  </p>
                  <p className="mt-2">
                    This means the app works even when the mentee has no
                    account — they just need a valid device serial.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Section>

          {/* 5. App Store Page */}
          <Section id="appstore" icon={Store} title="App Store Page (Index.tsx)">
            <p>
              This is the main screen — what users see when they open the app.
              It has <strong>two completely different modes</strong>:
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-xl bg-muted/30 border border-border/50">
                <p className="font-medium text-foreground text-sm">
                  🍎 Apple App Store Mode
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  When <Code>hasAppleAppStore = true</Code>. User has full
                  native App Store access. We only show blocked/restricted apps
                  so they know what's off-limits.
                </p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border/50">
                <p className="font-medium text-foreground text-sm">
                  📱 Shomri App Store Mode
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Default mode. Shows allowed apps based on whitelist or
                  categories configuration.
                </p>
              </div>
            </div>

            <p className="font-medium text-foreground mt-4">Data sources:</p>
            <ul className="list-disc list-inside space-y-2 text-sm">
              <li>
                <Code>api.apps.list.allowedByDeviceFilter</Code> — paginated,
                serial-scoped. <strong>Always runs</strong> when a serial is
                available (we removed the mode gate to fix the mentee bug).
              </li>
              <li>
                <Code>api.apps.list.allowedByDeviceStatic</Code> —
                non-paginated, returns explicitly whitelisted apps.
              </li>
              <li>
                Results are <strong>merged and deduplicated</strong> by ID, then
                sorted alphabetically.
              </li>
            </ul>

            <p className="font-medium text-foreground mt-4">Features:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>
                <strong>Search:</strong> Debounced 300ms, triggers after 3+
                chars, uses backend <Code>query</Code> param
              </li>
              <li>
                <strong>Category filter:</strong> Dropdown built dynamically from
                loaded apps' categories
              </li>
              <li>
                <strong>Infinite scroll:</strong> <Code>useInfiniteScroll</Code>{" "}
                hook loads more pages as you scroll
              </li>
              <li>
                <strong>Pull-to-refresh:</strong> Visual indicator only — Convex
                auto-refreshes reactively
              </li>
              <li>
                <strong>Hidden apps:</strong> WebKit and Safari are always
                filtered out from results
              </li>
            </ul>
          </Section>

          {/* 6. App Detail */}
          <Section id="appdetail" icon={Package} title="App Detail Page">
            <p>
              When you tap an app, you land on the detail page. It fetches data
              from the <strong>iTunes API</strong> (not Convex) via the{" "}
              <Code>useAppStoreDetails</Code> hook.
            </p>

            <p className="font-medium text-foreground mt-3">Install flow:</p>
            <div className="flex flex-wrap items-center gap-1 text-sm">
              <FlowStep>User taps "Install"</FlowStep>
              <FlowArrow />
              <FlowStep>
                <Code>showSKOverlay(appStoreId)</Code>
              </FlowStep>
              <FlowArrow />
              <FlowStep>Swift bridge</FlowStep>
              <FlowArrow />
              <FlowStep>Native App Store overlay</FlowStep>
            </div>

            <ul className="list-disc list-inside space-y-1 text-sm mt-3">
              <li>
                The SKOverlay dismiss callback resets the install button state
              </li>
              <li>
                A troubleshooting dialog appears after install is pressed (helps
                users if the overlay doesn't show)
              </li>
              <li>
                Back button navigates to <Code>/&#123;serial&#125;</Code>
              </li>
            </ul>
          </Section>

          {/* 7. Updates */}
          <Section id="updates" icon={RefreshCw} title="Updates Page">
            <p>
              Compares installed apps (from{" "}
              <Code>api.apps.list.updates</Code> action) against available apps
              to find ones that need version updates.
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Update button triggers SKOverlay for each app individually</li>
              <li>
                <strong>Gotcha:</strong> If the native App Store shows "Open"
                instead of "Update", the app is already current — this is normal
              </li>
            </ul>
          </Section>

          {/* 8. Requests */}
          <Section id="requests" icon={MessageSquare} title="Requests Page">
            <p>Two tabs: <strong>App Request</strong> and <strong>Web Request</strong>.</p>

            <Accordion type="single" collapsible>
              <AccordionItem value="app-request">
                <AccordionTrigger className="text-sm">
                  App Request Flow
                </AccordionTrigger>
                <AccordionContent>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>
                      User searches for an app using <Code>useItunesSearch</Code>
                    </li>
                    <li>Selects an app from the iTunes search results</li>
                    <li>
                      Submits via{" "}
                      <Code>api.requests.create.appRequest</Code> with serial,
                      bundleId, trackId, app name, icon URL, etc.
                    </li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="web-request">
                <AccordionTrigger className="text-sm">
                  Web Request Flow
                </AccordionTrigger>
                <AccordionContent>
                  <ol className="list-decimal list-inside space-y-2 text-sm">
                    <li>User enters a domain (protocol is stripped automatically)</li>
                    <li>
                      Domain is validated with a regex pattern
                    </li>
                    <li>
                      Submitted via{" "}
                      <Code>api.requests.create.webRequest</Code>
                    </li>
                  </ol>
                </AccordionContent>
              </AccordionItem>
            </Accordion>

            <p className="font-medium text-foreground mt-3">Request statuses:</p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>
                <strong>Pending:</strong> No <Code>responseTime</Code> set yet
              </li>
              <li>
                <strong>Approved:</strong> <Code>response</Code> is one of{" "}
                <Code>level</Code>, <Code>device</Code>, <Code>allow</Code>
              </li>
              <li>
                <strong>Rejected:</strong> <Code>response</Code> is{" "}
                <Code>deny</Code>
              </li>
            </ul>

            <p className="text-sm mt-2">
              <strong>Note:</strong> Self-managed users don't see the Requests
              tab at all — they manage their own settings directly.
            </p>
          </Section>

          {/* 9. Settings */}
          <Section id="settings" icon={Settings} title="Settings Overview">
            <p>
              The Settings page has three main sections, each linking to a
              sub-page:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>
                <strong>App Management</strong> — control which apps are
                available
              </li>
              <li>
                <strong>Browser Policies</strong> — control web browsing
                filtering
              </li>
              <li>
                <strong>General Restrictions</strong> — iOS MDM restrictions
                (coming soon)
              </li>
            </ul>
            <p className="text-sm mt-2">
              All settings pages use <Code>useEffectiveSerial</Code> to scope
              everything to the current device.
            </p>
          </Section>

          {/* 10. App Management */}
          <Section id="appmanagement" icon={Package} title="App Management">
            <p>
              Three modes determine how apps are made available. The mode is
              stored in the backend as <Code>appMode</Code>:
            </p>

            <div className="space-y-3 mt-3">
              {[
                {
                  mode: "categories",
                  label: "Apps by Category",
                  desc: "Categories are toggled on/off. Apps in enabled categories are automatically available. This is the most common mode.",
                },
                {
                  mode: "whitelist",
                  label: "Approved Apps Only",
                  desc: "Each app must be explicitly approved. No category-based filtering — it's all manual.",
                },
                {
                  mode: "blacklist",
                  label: "Open App Store + Block List",
                  desc: "User has access to the full Apple App Store, but specific apps can be blocked. Sets hasAppleAppStore = true.",
                },
              ].map(({ mode, label, desc }) => (
                <div
                  key={mode}
                  className="p-3 rounded-xl bg-muted/30 border border-border/50"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {mode}
                    </Badge>
                    <p className="font-medium text-foreground text-sm">
                      {label}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </div>
              ))}
            </div>

            <Accordion type="single" collapsible className="mt-3">
              <AccordionItem value="mode-inference">
                <AccordionTrigger className="text-sm">
                  Mode inference when no explicit appMode is set
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    If no explicit <Code>appMode</Code> exists, it's inferred
                    from the shape of the <Code>appFilter</Code> data. The
                    backend knows the real mode and handles queries correctly —
                    the frontend inference is just for UI display.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="add-flow">
                <AccordionTrigger className="text-sm">
                  Adding a specific app (whitelist/blacklist)
                </AccordionTrigger>
                <AccordionContent>
                  <ol className="list-decimal list-inside space-y-1 text-sm">
                    <li>Search the Shomri catalog first</li>
                    <li>If not found → search iTunes</li>
                    <li>If still not found → user can submit a request</li>
                  </ol>
                  <p className="mt-2">
                    Mentors and self-managed users do a direct mutation.
                    Mentees/children see a "request submitted" toast (but there's
                    no actual request mutation for settings changes yet).
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Section>

          {/* 11. Browser */}
          <Section id="browser" icon={Globe} title="Browser Settings">
            <p>
              Three browser modes, stored as <Code>browserMode</Code>:
            </p>

            <div className="space-y-3 mt-3">
              {[
                {
                  mode: "categories",
                  label: "Filtered Browser",
                  desc: "Category-based filtering with allowlist and blocklist overrides. Most flexible option.",
                },
                {
                  mode: "whitelist",
                  label: "Whitelisted Browser",
                  desc: "Only explicitly approved sites can be visited. Very restrictive.",
                },
                {
                  mode: "none",
                  label: "No Browser",
                  desc: "Browser is completely disabled. User can't browse the web at all.",
                },
              ].map(({ mode, label, desc }) => (
                <div
                  key={mode}
                  className="p-3 rounded-xl bg-muted/30 border border-border/50"
                >
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="text-xs">
                      {mode}
                    </Badge>
                    <p className="font-medium text-foreground text-sm">
                      {label}
                    </p>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </div>
              ))}
            </div>

            <Accordion type="single" collapsible className="mt-3">
              <AccordionItem value="bluejay">
                <AccordionTrigger className="text-sm">
                  BlueJay rules structure (important!)
                </AccordionTrigger>
                <AccordionContent>
                  <div className="space-y-2 text-sm">
                    <p>
                      The <Code>blueJayRules</Code> object controls all browser
                      filtering:
                    </p>
                    <pre className="bg-muted/50 p-3 rounded-lg text-xs overflow-x-auto">
{`{
  categories: string[],   // BLOCKED categories (inverted!)
  allowlist: string[],     // Always allowed domains
  blocklist: string[],     // Always blocked domains
  config: {
    nsfwLevel: "light" | "medium" | "strict",
    human: boolean,        // Blur images with humans
    accuracy: number       // Detection confidence
  }
}`}
                    </pre>
                    <div className="p-2 rounded-lg bg-destructive/10 border border-destructive/20">
                      <p className="text-xs">
                        <AlertTriangle className="inline w-3 h-3 mr-1" />
                        <strong>⚠️ Inverted logic:</strong> Categories listed in{" "}
                        <Code>blueJayRules.categories</Code> are{" "}
                        <strong>BLOCKED</strong>, not allowed. This catches
                        people off guard.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Section>

          {/* 12. General Restrictions */}
          <Section id="restrictions" icon={Lock} title="General Restrictions">
            <p>
              Currently shows a <strong>"Coming Soon"</strong> maintenance
              banner. The backend hook (
              <Code>useGeneralRestrictions</Code>) exists and reads from{" "}
              <Code>api.devices.restrictions.get</Code>.
            </p>
            <p className="text-sm">
              When enabled, it will map iOS MDM restriction keys (like{" "}
              <Code>allowCamera</Code>, <Code>allowSiri</Code>,{" "}
              <Code>allowScreenShot</Code>) to toggle switches in the UI.
            </p>
          </Section>

          {/* 13. Native Bridge */}
          <Section id="native" icon={Cpu} title="Native iOS Bridge">
            <p>
              The app communicates with the native iOS wrapper via{" "}
              <Code>window.webkit.messageHandlers</Code>:
            </p>

            <div className="space-y-3 mt-3">
              <div className="p-3 rounded-xl bg-muted/30 border border-border/50 text-sm">
                <p className="font-medium text-foreground">
                  <Code>showSKOverlay(appStoreId)</Code>
                </p>
                <p className="text-muted-foreground mt-1">
                  Posts a message to{" "}
                  <Code>
                    window.webkit.messageHandlers.showSKOverlay
                  </Code>
                  . The iOS app shows the native App Store product overlay so
                  users can install without leaving the app.
                </p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border/50 text-sm">
                <p className="font-medium text-foreground">
                  <Code>closeSKOverlay()</Code>
                </p>
                <p className="text-muted-foreground mt-1">
                  Sends <Code>null</Code> to the same handler to dismiss the
                  overlay programmatically.
                </p>
              </div>
              <div className="p-3 rounded-xl bg-muted/30 border border-border/50 text-sm">
                <p className="font-medium text-foreground">
                  <Code>window.onSKOverlayDismissed</Code>
                </p>
                <p className="text-muted-foreground mt-1">
                  Callback registered on window. iOS calls this when the user
                  dismisses the overlay. The web app uses it to reset the
                  install button state.
                </p>
              </div>
            </div>

            <p className="text-sm mt-3">
              <strong>Fallback:</strong> In non-WebView environments (like a
              desktop browser), these functions just{" "}
              <Code>console.log</Code> instead of crashing.
            </p>
          </Section>

          {/* 14. Edge Cases */}
          <Section
            id="edgecases"
            icon={AlertTriangle}
            title="Edge Cases & Gotchas"
          >
            <Accordion type="multiple" className="space-y-0">
              <AccordionItem value="mentee-mapping">
                <AccordionTrigger className="text-sm">
                  Mentee with no Clerk-to-Convex mapping
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    The serial resolves via header/path/localStorage, but{" "}
                    <Code>deviceConfig</Code> stays null. Previously this caused
                    the app to default to whitelist mode and show no apps. Fixed
                    by always running the paginated query regardless of detected
                    mode — the backend handles the real filtering.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="stubbed-hooks">
                <AccordionTrigger className="text-sm">
                  Stubbed-out hooks (useBlockedApps, useUserIdentity)
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    <Code>useBlockedApps</Code> and{" "}
                    <Code>useUserIdentity</Code> both return empty/default data.
                    They call admin-only Convex endpoints that regular users
                    can't access. They're stubbed out to prevent "Not authorized"
                    crashes.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="radix-bug">
                <AccordionTrigger className="text-sm">
                  Radix UI pointer-events bug
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    Radix UI sometimes leaves{" "}
                    <Code>pointer-events: none</Code> on the body after dialogs
                    close. The AppManagement page has manual cleanup timers to
                    work around this.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="self-managed">
                <AccordionTrigger className="text-sm">
                  Self-managed users and admin queries
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    Self-managed users have a Clerk session but NOT the
                    support/admin role. Convex queries like{" "}
                    <Code>api.users.get.me</Code> and{" "}
                    <Code>api.groups.list.all</Code> require admin privileges.
                    The app skips these queries for self-managed users to avoid
                    "Not authorized" errors.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="identity-default">
                <AccordionTrigger className="text-sm">
                  Identity defaults to restricted
                </AccordionTrigger>
                <AccordionContent>
                  <p>
                    When the user's identity can't be determined (common for
                    mentees), the app defaults to a restricted (mentee-like)
                    behavior. This prevents accidentally calling admin endpoints
                    and causing errors.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="deviceconfig-chain">
                <AccordionTrigger className="text-sm">
                  deviceConfig resolution chain
                </AccordionTrigger>
                <AccordionContent>
                  <div className="flex flex-wrap items-center gap-1 text-sm">
                    <FlowStep>
                      <Code>Clerk user</Code>
                    </FlowStep>
                    <FlowArrow />
                    <FlowStep>
                      <Code>Convex user</Code>
                    </FlowStep>
                    <FlowArrow />
                    <FlowStep>
                      <Code>device</Code>
                    </FlowStep>
                    <FlowArrow />
                    <FlowStep>
                      <Code>deviceConfig</Code> string
                    </FlowStep>
                  </div>
                  <p className="mt-2">
                    This string is used as the key for{" "}
                    <Code>api.devices.apps.getConfig</Code> and{" "}
                    <Code>api.devices.policies.get</Code>. If any step in the
                    chain fails, deviceConfig is null and those queries are
                    skipped.
                  </p>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </Section>

          {/* 15. External Services */}
          <Section id="services" icon={Cloud} title="External Services">
            <div className="space-y-3">
              {[
                {
                  name: "Clerk",
                  desc: "Authentication — sign-in, sign-out, password reset. User identity management.",
                },
                {
                  name: "Convex",
                  desc: "Backend — all queries, mutations, and actions. Real-time reactive updates. The source of truth for everything.",
                },
                {
                  name: "Gleap",
                  desc: "In-app support chat + bug reporting. Initialized with API key on app load. User identified after Clerk sign-in.",
                },
                {
                  name: "iTunes Search API",
                  desc: "App search for the Requests page. Proxied through the release0 service. Used by the useItunesSearch hook.",
                },
                {
                  name: "PostHog",
                  desc: "Analytics. Imported but minimal usage currently.",
                },
                {
                  name: "HyperDX",
                  desc: "Browser monitoring and observability.",
                },
              ].map(({ name, desc }) => (
                <div
                  key={name}
                  className="p-3 rounded-xl bg-muted/30 border border-border/50"
                >
                  <p className="font-medium text-foreground text-sm">{name}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{desc}</p>
                </div>
              ))}
            </div>
          </Section>

          {/* Footer */}
          <div className="border-t border-border/50 pt-8 mt-12 text-center text-xs text-muted-foreground">
            <p>
              This page is not linked anywhere in the app. Share the URL only
              with Shomri staff.
            </p>
            <p className="mt-1">
              Last updated based on codebase analysis. If something's wrong,
              ping the dev team.
            </p>
          </div>
        </main>
      </div>
    </div>
  );
};

export default InternalDocs;
